import * as React from 'react';
import { useIntl } from 'react-intl';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { useVirtualizer } from '@tanstack/react-virtual';
import { CmdChips, CmdLoader } from '@commander-services/gui-components';
import { useQueryClient } from '@tanstack/react-query';
import {
  AreaFilterOptions,
  IDetailWaypointResponseData,
  IFiltersWaypoint,
  IVerifyDeleteRequestData,
  IWaypoint,
} from './types';
import PermissionsService, {
  PERMISSION_WAYPOINT_CUSTOMER,
  PERMISSION_WAYPOINT_PRIVATE,
} from '../../../Services/PermissionsService';
import { deleteWaypoint, verifyRemoveWaypoint, filterBinary } from './WaypointService';
import ConfirmDialog from '../../ConfirmDialog';
import { selectedCustomersAtom } from '../../../store/recoil/customers';
import { WAYPOINT_QUERY_KEY, WAYPOINT_WORD_LENGTH } from './constants';
import WaypointIconService from './WaypointIconService';
import {
  activeFilterAtom,
  detailWaypointAtom,
  filteredWaypointsAtom,
  filteredWaypointsStringAtom,
  filterOptionsWpAtom,
  isWaypointsOpenAtom,
  onHoverWaypointIdAtom,
  removedWaypointIdAtom,
  showWaypointLoaderAtom,
  waypointDataAtom,
  waypointIdAtom,
  waypointsAtom,
} from './WaypointState';
import { autofocusAtom } from '../OnlineMapState';
import useAnalytics from '../../../hooks/useAnalytics';

const WAYPOINT_VERIFY_TYPE_ALARM = 'alarm';
const WAYPOINT_PRIVATE = 'private';
const WAYPOINT_CUSTOMER = 'customer';

interface IWaypointForm {
  filterWaypointOpen: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
  detailWaypointOpen: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
  addWaypointOpen: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  editWaypointOpen: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
  copyWaypointOpen: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
}

function WaypointForm(props: IWaypointForm) {
  const { formatMessage: f } = useIntl();
  const [isOpenConfirmDialog, setIsOpenConfirmDialog] = React.useState(false);
  const [waypoints, setWaypoints] = useRecoilState<IWaypoint[]>(waypointsAtom);
  const [waypointData, setWaypointData] = useRecoilState<IDetailWaypointResponseData | null>(
    waypointDataAtom
  );
  const setRemovedWaypointId = useSetRecoilState<number | null>(removedWaypointIdAtom);
  const setOnHoverWaypointId = useSetRecoilState<number | null>(onHoverWaypointIdAtom);
  const [filteredWaypointsString, setFilteredWaypointsString] = useRecoilState<string>(
    filteredWaypointsStringAtom
  );
  const [filteredWaypoints, setFilteredWaypoints] =
    useRecoilState<IWaypoint[]>(filteredWaypointsAtom);
  const [activeFilter, setActiveFilter] = useRecoilState(activeFilterAtom);

  const [isWaypointsOpen] = useRecoilState<boolean>(isWaypointsOpenAtom);
  const [detailWaypoint, _setDetailWaypoint] = useRecoilState<IDetailWaypointResponseData | null>(
    detailWaypointAtom
  );
  const [verifyDeleteData, setVerifyDeleteData] = React.useState<IVerifyDeleteRequestData | null>(
    null
  );
  const [waypointReferenceTitle, setWaypointReferenceTitle] = React.useState<string[]>([]);
  const [selectedWaypointId, setWaypointId] = useRecoilState<number | null>(waypointIdAtom);
  const showWaypointLoader = useRecoilValue(showWaypointLoaderAtom);
  const queryClient = useQueryClient();
  const setAutofocus = useSetRecoilState<boolean>(autofocusAtom);
  const selectedCustomers = useRecoilValue<number[]>(selectedCustomersAtom);
  const { trackEvent } = useAnalytics();
  const filterDataOptions = useRecoilValue(filterOptionsWpAtom);
  const [expandedFuelIds, setExpandedFuelIds] = React.useState<number[]>([]);

  const parentRef = React.useRef<HTMLDivElement>(null);

  const virtualizer = useVirtualizer({
    count: filteredWaypointsString.length > 0 ? filteredWaypoints.length : waypoints.length,
    getScrollElement: () => parentRef.current,
    estimateSize: (index) => {
      const wp = filteredWaypointsString.length > 0 ? filteredWaypoints[index] : waypoints[index];
      if (!wp) return 35;

      const isExpanded = typeof wp.id === 'number' && expandedFuelIds.includes(wp.id);
      const numFuels = wp?.refueling?.fuels?.length || 0;

      const visibleFuels = isExpanded ? numFuels : Math.min(numFuels, 2);
      const baseHeight = 35;
      const fuelLineHeight = 20;
      const toggleButtonHeight = numFuels > 2 ? 24 : 0;

      return baseHeight + visibleFuels * fuelLineHeight + toggleButtonHeight;
    },
    overscan: 5,
  });
  const toggleFuelList = (id: number) => {
    setExpandedFuelIds((prev) => {
      const newIds = prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id];

      setTimeout(() => {
        virtualizer.measure();
      }, 0);

      return newIds;
    });
  };

  const handleClickWaypoint = React.useCallback(
    async (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      const waypointId = event.currentTarget.getAttribute('data-id');
      trackEvent(['map', 'click', 'Select waypoint from list']);

      if (String(waypointId) !== String(selectedWaypointId)) {
        setWaypointId(Number(waypointId));
      } else {
        setWaypointId(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedWaypointId]
  );

  // const handleMouseEnter = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
  //   const waypointId = event.currentTarget.getAttribute('data-id');
  //   setOnHoverWaypointId(Number(waypointId));
  // };

  // const handleMouseLeave = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
  //   setOnHoverWaypointId(null);
  // };

  const handleOnChangeSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    trackEvent(['map', 'click', 'Search Waypoint']);
    setFilteredWaypointsString(event.currentTarget.value);
    setAutofocus(true);
  };

  const handleCancelSearch = () => {
    setFilteredWaypointsString('');
  };

  const updateWaypoints = () => {
    queryClient.invalidateQueries({
      queryKey: [WAYPOINT_QUERY_KEY, selectedCustomers],
    });
  };

  const handleAreaTypeIcon = (type: string) => {
    if (type === 'commercial') {
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
        >
          <path
            d="M0.75375,16.08 C0.33768,16.08 0,15.7473451 0,15.3374668 C0,14.9275884 0.33768,14.5949336 0.75375,14.5949336 L1.005,14.594 L1.005,3.61732239 C1.005,2.92033121 1.426095,2.29363317 2.07834,2.02236103 L6.60084,0.137316691 C7.14555,-0.0894034529 7.76463,-0.032970928 8.25708,0.288793468 C8.750535,0.610557865 9.043995,1.15112205 9.043995,1.7332681 L9.043995,3.45693522 C9.043995,3.86681356 8.706315,4.19946844 8.290245,4.19946844 C7.874175,4.19946844 7.536495,3.86681356 7.536495,3.45693522 L7.536495,1.7332681 C7.536495,1.61743291 7.466145,1.55407008 7.423935,1.52634884 C7.381725,1.4986276 7.295295,1.45902583 7.186755,1.50555791 L2.664255,3.38961221 C2.571795,3.42822393 2.511495,3.51732792 2.511495,3.61732239 L2.511,14.594 L6.03,14.5949336 L6.03,6.92209026 C6.03,5.96669751 6.818925,5.18951274 7.78875,5.18951274 L13.31625,5.18951274 C14.286075,5.18951274 15.075,5.96669751 15.075,6.92209026 L15.075,14.5949336 L15.32625,14.5949336 C15.74232,14.5949336 16.08,14.9275884 16.08,15.3374668 C16.08,15.7473451 15.74232,16.08 15.32625,16.08 Z M9.29625,11.1297785 C8.88018,11.1297785 8.5425,11.4624334 8.5425,11.8723117 L8.5425,12.3673339 C8.5425,12.7772122 8.88018,13.1098671 9.29625,13.1098671 C9.71232,13.1098671 10.05,12.7772122 10.05,12.3673339 L10.05,11.8723117 C10.05,11.4624334 9.71232,11.1297785 9.29625,11.1297785 Z M11.80875,11.1297785 C11.39268,11.1297785 11.055,11.4624334 11.055,11.8723117 L11.055,12.3673339 C11.055,12.7772122 11.39268,13.1098671 11.80875,13.1098671 C12.22482,13.1098671 12.5625,12.7772122 12.5625,12.3673339 L12.5625,11.8723117 C12.5625,11.4624334 12.22482,11.1297785 11.80875,11.1297785 Z M9.29625,8.15964563 C8.88018,8.15964563 8.5425,8.49230051 8.5425,8.90217885 L8.5425,9.397201 C8.5425,9.80707934 8.88018,10.1397342 9.29625,10.1397342 C9.71232,10.1397342 10.05,9.80707934 10.05,9.397201 L10.05,8.90217885 C10.05,8.49230051 9.71232,8.15964563 9.29625,8.15964563 Z M11.80875,8.15964563 C11.39268,8.15964563 11.055,8.49230051 11.055,8.90217885 L11.055,9.397201 C11.055,9.80707934 11.39268,10.1397342 11.80875,10.1397342 C12.22482,10.1397342 12.5625,9.80707934 12.5625,9.397201 L12.5625,8.90217885 C12.5625,8.49230051 12.22482,8.15964563 11.80875,8.15964563 Z"
            fill="#737373"
          />
        </svg>
      );
    }
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        // className="cmd-tag-ref"
      >
        <path
          d="M8.04,4.68557217 L14.7394556,8.74472348 L14.7394556,13.6330435 C14.7394556,14.9823652 13.6876411,16.08 12.3946461,16.08 L9.04491834,16.08 L9.04491834,12.933913 C9.04491834,12.355033 8.59471492,11.8852174 8.04,11.8852174 C7.48528508,11.8852174 7.03508166,12.355033 7.03508166,12.933913 L7.03508166,16.08 L3.68535386,16.08 C2.39235892,16.08 1.34054439,14.9823652 1.34054439,13.6330435 L1.34054439,8.74472348 L8.04,4.68697043 Z M8.54513895,0.142622609 L15.5795673,4.40452174 C16.0592484,4.69536 16.2227151,5.33716174 15.9440177,5.83773913 C15.7577729,6.17332174 15.4201203,6.3606887 15.0744284,6.3606887 C14.9029223,6.35929043 14.7300764,6.31314783 14.5706293,6.21666783 L8.04,2.26098783 L1.50937067,6.21666783 C1.03236944,6.50750609 0.414679629,6.33691826 0.135982275,5.83634087 C-0.142715078,5.33576348 0.0207516391,4.69396174 0.50043266,4.40312348 L7.53620094,0.142622609 C7.84705568,-0.0475408696 8.23428421,-0.0475408696 8.54513895,0.142622609 Z"
          fill="#737373"
        />
      </svg>
    );
  };

  const renderWaypointRow = (wp: IWaypoint, style: React.CSSProperties) => {
    const isActive = wp.id === selectedWaypointId;
    const isExpanded = typeof wp.id === 'number' && expandedFuelIds.includes(wp.id);
    const hasMoreThanTwoFuels = wp?.refueling?.fuels.length > 2;

    const nameDisplay =
      wp?.name && String(wp.name).length > WAYPOINT_WORD_LENGTH
        ? `${String(wp.name).substring(0, WAYPOINT_WORD_LENGTH)} ...`
        : wp.name;

    return (
      <a
        id={String(wp.id)}
        key={String(wp.id)}
        data-id={wp.id}
        href="#"
        style={style}
        className={`${isActive ? 'item active' : 'item'} flex flex-col gap-[8px] text-[#737373]`}
        onClick={handleClickWaypoint}
        title={wp.name}
      >
        <div className="flex justify-between items-center w-full">
          <div className="flex items-center gap-2 overflow-hidden">
            <i
              className={`cmd-icon cmd-icon-${WaypointIconService.getCssIcon(wp.icon || '')} text-[#737373]`}
            />
            <span className="truncate text-[13px]">{nameDisplay}</span>
          </div>

          <div className="flex items-center gap-2 whitespace-nowrap">
            {wp?.refueling?.type && handleAreaTypeIcon(wp.refueling.type)}
            {wp.type === 'private' && (
              <i className="cmd-icon cmd-icon-preview-2 inline-block text-[#737373]" />
            )}
            <span>{wp.customerLabel}</span>
          </div>
        </div>

        {Array.isArray(wp?.refueling?.fuels) && wp.refueling.fuels.length > 0 && (
          <div className="text-[11px] pl-[25px] space-y-[4px]">
            {(isExpanded ? wp.refueling.fuels : wp.refueling.fuels.slice(0, 2)).map((fuel, i) => (
              <div key={i} className="flex justify-between">
                <span className="truncate">{fuel.name}</span>
                <span className="whitespace-nowrap">
                  {fuel.unitPriceIncVat.toFixed(2)} {fuel.currency}/{fuel.unit}
                </span>
              </div>
            ))}

            {hasMoreThanTwoFuels && typeof wp.id === 'number' && (
              <div className="flex justify-center">
                <button
                  className="text-[11px] text-[#737373] no-underline cursor-pointer"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (typeof wp.id === 'number') {
                      toggleFuelList(wp.id);
                      trackEvent([
                        'map',
                        'click',
                        isExpanded ? 'Collapse area list' : 'Expand area list',
                      ]);
                    }
                  }}
                >
                  {isExpanded
                    ? `${f({ id: 'enum.area.list.show.less' })}`
                    : `${f({ id: 'enum.area.list.show.more' })}`}
                  {isExpanded ? (
                    <i className="cmd-icons cmd-icon-dropup" />
                  ) : (
                    <i className="cmd-icons cmd-icon-dropdown" />
                  )}
                </button>
              </div>
            )}
          </div>
        )}
      </a>
    );
  };

  const handleDeleteWaypoint = async (_event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (!waypointData || !selectedWaypointId) {
      return;
    }
    setWaypoints([]);
    setIsOpenConfirmDialog(false);
    setWaypointId(null);
    // dispatch(onlineMapActions.setWaypointId(false));

    const response = await deleteWaypoint(selectedWaypointId);

    updateWaypoints();
    // setWaypointsLoaded(false);
    if (response) {
      setVerifyDeleteData(null);
      setRemovedWaypointId(selectedWaypointId);
      setOnHoverWaypointId(null);
    }
  };

  const handleConfirmDialog = async () => {
    trackEvent(['map', 'click', 'Delete waypoint']);
    if (detailWaypoint) {
      setWaypointData(detailWaypoint);
    }

    if (typeof selectedWaypointId === 'number') {
      setWaypointReferenceTitle([]);
      const verifyDelete = await verifyRemoveWaypoint(selectedWaypointId);

      if (verifyDelete) {
        const findType = verifyDelete.details.find(
          (item) => item.type === WAYPOINT_VERIFY_TYPE_ALARM
        );
        if (findType) {
          const tempTitle = findType.details.map((item) => item.name);
          setWaypointReferenceTitle(tempTitle);
        }

        setVerifyDeleteData(verifyDelete);
        setIsOpenConfirmDialog(true);
      }
    }
  };

  const handleCloseConfirmDialog = () => {
    setIsOpenConfirmDialog(false);
  };

  const handleShowSelectedElement = React.useCallback(() => {
    if (typeof selectedWaypointId === 'number' && isWaypointsOpen) {
      const items = filteredWaypointsString.length > 0 ? filteredWaypoints : waypoints;
      const scrollToIndex = items.findIndex((wp) => wp.id === selectedWaypointId);

      if (scrollToIndex >= 0) {
        virtualizer.scrollToIndex(scrollToIndex, { align: 'center' });
      }
    }
  }, [
    isWaypointsOpen,
    selectedWaypointId,
    filteredWaypointsString,
    waypoints,
    filteredWaypoints,
    virtualizer,
  ]);

  React.useEffect(() => {
    handleShowSelectedElement();
  }, [handleShowSelectedElement]);

  React.useEffect(() => {
    setFilteredWaypoints(filterBinary(filteredWaypointsString, waypoints));
  }, [filteredWaypointsString, setFilteredWaypoints, waypoints]);

  const handleCreatePermissions = (): boolean => {
    let tempPermissions = 0;

    if (isWaypointsOpen) {
      selectedCustomers.forEach((item) => {
        const permission =
          PermissionsService.hasAccess(PERMISSION_WAYPOINT_CUSTOMER, ['create'], item) ||
          PermissionsService.hasAccess(PERMISSION_WAYPOINT_PRIVATE, ['create'], item);

        if (permission) {
          tempPermissions += 1;
        }
      });
    }

    if (tempPermissions > 0) {
      return true;
    }

    return false;
  };

  const handlePermissions = (
    detailWp: IDetailWaypointResponseData,
    permissionType: string
  ): boolean => {
    if (detailWp && isWaypointsOpen) {
      if (detailWp.type === WAYPOINT_CUSTOMER) {
        return PermissionsService.hasAccess(
          PERMISSION_WAYPOINT_CUSTOMER,
          [permissionType],
          Number(detailWp.customerId)
        );
      }
      if (detailWp.type === WAYPOINT_PRIVATE) {
        return PermissionsService.hasAccess(
          PERMISSION_WAYPOINT_PRIVATE,
          [permissionType],
          Number(detailWp.customerId)
        );
      }
    }
    return false;
  };

  const removeFilter = (key: keyof IFiltersWaypoint) => {
    setActiveFilter((prev) => {
      if (!prev) return prev;

      const { [key]: _, ...rest } = prev;

      const isEmpty = Object.values(rest).every((value) => value === 0 || value === null);

      return isEmpty ? null : (rest as IFiltersWaypoint);
    });
  };

  const truncateLabel = (label: string, maxLength = 10) =>
    label.length > maxLength ? `${label.slice(0, maxLength)}…` : label;

  const renderFilters = (): {
    component: React.ReactNode;
    count: number;
    truncatedCount: number;
  } => {
    if (!filterDataOptions || !activeFilter)
      return { component: null, count: 0, truncatedCount: 0 };

    const filterKeyMap: Record<string, keyof AreaFilterOptions> = {
      refuelingAreaType: 'refuelingTypeList',
      areaType: 'areaType',
    };

    const resolveAreaTypeLabel = (filter: IFiltersWaypoint) => {
      if (filter.refueling && filter.waypoint) return 'waypointAndFuelArea';
      if (filter.refueling && !filter.waypoint) return 'fuelArea';
      if (!filter.refueling && filter.waypoint) return 'waypoint';
      return null;
    };

    const areaTypeKey = resolveAreaTypeLabel(activeFilter);
    const filterItems: { id: string; fileName: string }[] = [];

    if (areaTypeKey) {
      const areaOption = filterDataOptions.areaType?.find((o) => o.value === areaTypeKey);
      if (areaOption) {
        filterItems.push({
          id: 'areaType',
          fileName: truncateLabel(areaOption.item),
        });
      }
    }

    Object.entries(activeFilter)
      .filter(([_key, value]) => value !== 0 && value !== null)
      .filter(([key]) => key !== 'refueling' && key !== 'waypoint')
      .forEach(([key, value]) => {
        const mappedKey = filterKeyMap[key] || key;
        const options = filterDataOptions[mappedKey] ?? [];
        const found = options.find((o) => String(o.value) === String(value));

        filterItems.push({
          id: key,
          fileName: truncateLabel(found?.item || String(value)),
        });
      });

    if (filterItems.length === 0) return { component: null, count: 0, truncatedCount: 0 };

    let truncatedCount = 0;
    filterItems.forEach((item) => {
      if (item.fileName.includes('…')) {
        truncatedCount += 1;
      }
    });

    const handleRemoveFilter = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      event.preventDefault();
      const key = event.currentTarget.getAttribute('data-file');

      if (!key) return;

      if (key === 'areaType') {
        removeFilter('waypoint');
        removeFilter('refueling');
      } else {
        removeFilter(key as keyof IFiltersWaypoint);
      }
    };

    return {
      component: (
        <CmdChips items={filterItems} handleRemoveFile={handleRemoveFilter} color="#b50536" />
      ),
      count: filterItems.length,
      truncatedCount,
    };
  };

  const { component: filtersComponent, count: filtersCount, truncatedCount } = renderFilters();

  React.useEffect(() => {
    virtualizer.measure(); // recalculates all row heights
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waypoints, filteredWaypoints]);

  return (
    <>
      {isOpenConfirmDialog && waypointData && waypointData.name && (
        <ConfirmDialog
          title={`${f(
            {
              id: 'waypoint.confirm.dialog.title',
            },
            { name: waypointData.name }
          )}`}
          cancelCallback={handleCloseConfirmDialog}
          continueCallback={handleDeleteWaypoint}
          cancelButtonTitle={f({
            id: 'waypoint.confirm.dialog.cancel',
          })}
          continueButtonTitle={f({
            id: 'table.deleteBtn',
          })}
          tooltip={waypointReferenceTitle.join('\n')}
        >
          {verifyDeleteData && verifyDeleteData?.details?.[0]
            ? `${f({ id: 'enum.area.remove.message' })} (${verifyDeleteData.references}): ${waypointReferenceTitle.join(',\n')}. 
            ${f({ id: 'enum.area.remove.message.question' })}`
            : f(
                {
                  id: 'enum.area.remove.dialog.message',
                },
                { name: waypointData.name }
              )}
        </ConfirmDialog>
      )}
      <div className="b-mc-waypoints">
        <div
          className={`b-mc-waypoints-filter ${filtersCount > 1 && truncatedCount > 1 ? 'pb-0' : ''}`}
        >
          <div className="row align-items-center">
            <div className="col pr-2">
              <div className="w-form-control">
                <input
                  type="text"
                  className="form-control"
                  placeholder={f({ id: 'waypoints.search.placeholder' })}
                  value={filteredWaypointsString}
                  onChange={handleOnChangeSearch}
                  data-cy="Waypoints-search"
                />
                <i className="cmd-icon cmd-icon-search" />
                {filteredWaypointsString.length > 0 && (
                  <a onClick={handleCancelSearch} className="cmd-icon cmd-icon-remove remove">
                    {/* &nbsp; */}
                  </a>
                )}
              </div>
            </div>
            <div className="col-auto pl-0">
              <a
                href="#"
                className={`e-button-sq border-on ${activeFilter ? 'bubble-on' : ''}`}
                onClick={props.filterWaypointOpen}
                title={f({ id: 'waypoints.form.filter.title' })}
              >
                <i className="cmd-icons cmd-icon-filter" />
                <span className="sr-only">{f({ id: 'waypoints.form.filter.title' })}</span>
              </a>
            </div>
          </div>

          {filtersCount === 0 ? (
            <div className="h-[4px]" />
          ) : filtersCount > 0 && truncatedCount < 2 ? (
            <div className="flex items-center gap-2 overflow-x-hidden overflow-y-hidden whitespace-nowrap pt-[4px] pb-[10px] h-[38px]">
              {filtersComponent}
            </div>
          ) : (
            <div className="b-waypoints-chip-scrollbar">{filtersComponent}</div>
          )}

          <div className="b-mc-waypoints-filter-actions">
            {isWaypointsOpen && handleCreatePermissions() ? (
              <button
                type="button"
                className="e-button-sq"
                title={f({ id: 'table.add' })}
                onClick={props.addWaypointOpen}
                data-cy="add_waypoint"
              >
                <i className="cmd-icons cmd-icon-add" />
                <span className="sr-only">{f({ id: 'table.add' })}</span>
              </button>
            ) : (
              isWaypointsOpen &&
              (PermissionsService.hasAccess(
                PERMISSION_WAYPOINT_CUSTOMER,
                ['read'],
                selectedCustomers
              ) ||
                PermissionsService.hasAccess(
                  PERMISSION_WAYPOINT_PRIVATE,
                  ['read'],
                  selectedCustomers
                )) && (
                <a
                  href="#"
                  className="e-button-sq e-button-sq--disabled"
                  title={f({ id: 'table.add' })}
                  data-cy="add_waypoint_disabled"
                >
                  <i className="cmd-icons cmd-icon-add" />
                  <span className="sr-only">{f({ id: 'table.add' })}</span>
                </a>
              )
            )}
          </div>
        </div>

        <div className="b-waypoints-list">
          {/* <InfiniteLoader isRowLoaded={waypointsLoaded}>
            {() => ( */}
          {/* <ScrollSync>
            {({ onScroll }) => ( */}
          {filteredWaypointsString.length > 0 && filteredWaypoints.length === 0 && (
            <a href="#" className="item">
              {f({ id: 'waypoints.missing' })}
            </a>
          )}
          {waypoints.length === 0 && (
            <a href="#" className="item">
              {f({ id: 'waypoints.missing' })}
            </a>
          )}
          <div className="b-waypoints-list-items">
            {showWaypointLoader && isWaypointsOpen && <CmdLoader inContent={true} />}

            {showWaypointLoader ? (
              <a href="#" className="item">
                {f({ id: 'waypoints.loading' })}
              </a>
            ) : (
              <div ref={parentRef} className="h-[340px] w-full overflow-y-auto">
                <div
                  style={{
                    height: `${virtualizer.getTotalSize()}px`,
                    position: 'relative',
                  }}
                >
                  {virtualizer.getVirtualItems().map((virtualRow) => {
                    const index = virtualRow.index;
                    const wp =
                      filteredWaypointsString.length > 0
                        ? filteredWaypoints[index]
                        : waypoints[index];

                    const style: React.CSSProperties = {
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: `${virtualRow.size}px`,
                      transform: `translateY(${virtualRow.start}px)`,
                    };

                    return (
                      <React.Fragment key={virtualRow.key}>
                        {wp && renderWaypointRow(wp, style)}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          {/* )}
          </ScrollSync> */}
          {/* )} */}
          {/* </InfiniteLoader>  */}
          {selectedWaypointId && (
            <div className="b-waypoints-list-actions">
              <a
                href="#"
                className="e-button-sq m-0"
                title={f({ id: 'table.detail' })}
                onClick={props.detailWaypointOpen}
                data-cy="waypoints_detail"
              >
                <i className="cmd-icons cmd-icon-detail" />
                <span className="sr-only">{f({ id: 'table.detail' })}</span>
              </a>
              {detailWaypoint && handlePermissions(detailWaypoint, 'update') && (
                <a
                  href="#"
                  className="e-button-sq m-0"
                  title={f({ id: 'enum.area.edit.record' })}
                  onClick={props.editWaypointOpen}
                  data-cy="waypoints_edit"
                >
                  <i className="cmd-icons cmd-icon-edit" />
                  <span className="sr-only">{f({ id: 'enum.area.edit.record' })}</span>
                </a>
              )}
              {detailWaypoint && handlePermissions(detailWaypoint, 'create') && (
                <a
                  href="#"
                  className="e-button-sq m-0"
                  data-cy="waypoints_copy"
                  title={f({ id: 'enum.area.copy.title' })}
                  onClick={props.copyWaypointOpen}
                >
                  <i className="cmd-icons cmd-icon-duplicate" />
                  <span className="sr-only">{f({ id: 'enum.area.copy.title' })}</span>
                </a>
              )}
              {detailWaypoint && handlePermissions(detailWaypoint, 'delete') && (
                <a
                  href="#"
                  className="e-button-sq m-0"
                  title={f({ id: 'waypoint.confirm.dialog.confirm' })}
                  onClick={handleConfirmDialog}
                  data-cy="waypoints_delete"
                >
                  <i className="cmd-icons cmd-icon-bin" />
                  <span className="sr-only">{f({ id: 'waypoint.confirm.dialog.confirm' })}</span>
                </a>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default React.memo(WaypointForm);
